import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from '@gerdau/hefesto';
import { Banner, Icon } from '@gerdau/hefesto';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { ICustomerUserState } from '../../../../../store/ducks/access-management/types';
import {
  ECustomerUserPermissionType,
  IUser
} from '../../../../../store/ducks/user/types';
import { RemoveUserIcon, BannerContainer } from './styles';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { tableTaggingParams } from './tableTaggingParams';
import Table from '../../../../../components/Table';
import { GetFlag } from '../../../../../utils/featureFlag';
interface ICustomerUsersTableDataSource {
  key: number;
  user: ICustomerUserState;
  isAdmin: boolean;
  hasFabricationPermission: boolean;
  hasFinancialPermission: boolean;
  hasViewOrdersPermission: boolean;
  hasViewPricesPermission: boolean;
  hasInputOrdersPermission: boolean;
}

interface ICustomerUsersTableProps {
  users: ICustomerUserState[];
  customerId: string;
  updateUserPermission(
    user: IUser,
    customerUserPermissionType: ECustomerUserPermissionType,
    updateType: 'add' | 'remove'
  ): void;
  removeAllUserPermissions(user: IUser): void;
  isRemovingUser: boolean;
}

const CustomerUsersTable: React.FC<ICustomerUsersTableProps> = ({
  users,
  customerId,
  updateUserPermission,
  removeAllUserPermissions,
  isRemovingUser
}) => {
  const dispatch = useDispatch();
  const [customerUsersData, setCustomersUsersData] = useState<
    ICustomerUsersTableDataSource[]
  >([]);
  const userState = useSelector((state: ApplicationState) => state.user);
  const canShowFabricationOption =
    (userState.roles.fabricationViewCustomers &&
      userState.roles.fabricationViewCustomers.includes(customerId)) ||
    userState.isManager;

  const [selectedUser, setSelectedUser] = useState('');
  const bannerContainer = useRef<HTMLDivElement>(null);

  const flagIgnoreRestrictions = GetFlag('ignore_user_management_restrictions');

  const restrictedToManageUsers =
    useSelector(
      (state: ApplicationState) => state.user.userRestrictions.usersManagement
    ) && !flagIgnoreRestrictions;

  useEffect(() => {
    bannerContainer.current?.focus();
  }, [selectedUser]);

  useEffect(() => {
    const tableData: ICustomerUsersTableDataSource[] = users
      .map((user, idx) => {
        return {
          key: idx,
          user: user,
          isAdmin: user.roles.adminCustomers.includes(customerId),
          hasFabricationPermission:
            user.roles.fabricationViewCustomers?.includes(customerId) || false,
          hasFinancialPermission:
            user.roles.financialCustomers.includes(customerId),
          hasViewOrdersPermission:
            user.roles.ordersViewCustomers.includes(customerId),
          hasViewPricesPermission:
            user.roles.ordersViewPriceCustomers.includes(customerId),
          hasInputOrdersPermission:
            user.roles.ordersInputCustomers.includes(customerId)
        };
      })
      .sort((a, b) => {
        // admins primeiro
        if (a.isAdmin && !b.isAdmin) {
          return -1;
        } else if (!a.isAdmin && b.isAdmin) {
          return 1;
        } else {
          return 0;
        }
      });

    setCustomersUsersData(tableData);
  }, [updateUserPermission]);

  const onChangePermission = (
    newValue: boolean,
    user: IUser,
    customerUserPermissionType: ECustomerUserPermissionType
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    dispatch(
      sendTagging({
        screenName: 'LG:GestaoDeUsuarios',
        itemDescription:
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tableTaggingParams[customerUserPermissionType][newValue as any]
            .itemDescription,
        itemName:
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tableTaggingParams[customerUserPermissionType][newValue as any]
            .itemName,
        event: 'click'
      })
    );

    updateUserPermission(
      user,
      customerUserPermissionType,
      newValue ? 'add' : 'remove'
    );
  };

  const getColumns = () => {
    const columns = [
      {
        title: 'Usuário',
        dataIndex: 'user',
        width: 200,
        height: 500,
        render: (user: ICustomerUserState) => <>{user.email}</>,
        sorter: (
          a: ICustomerUsersTableDataSource,
          b: ICustomerUsersTableDataSource
        ) => a.user.email.localeCompare(b.user.email)
      },
      {
        title: 'Admin',
        dataIndex: 'isAdmin',
        width: 60,
        align: 'center' as const,
        render: (isAdmin: boolean, data: ICustomerUsersTableDataSource) => (
          <>
            <Checkbox
              name="admin"
              checked={isAdmin}
              onChange={(e) => {
                onChangePermission(
                  e.target.checked,
                  data.user,
                  ECustomerUserPermissionType.ADMIN
                );
              }}
              disabled={
                data.user.loadingUpdateUser ||
                isRemovingUser ||
                restrictedToManageUsers
              }
            />
          </>
        )
      },
      {
        title: 'Usuário de corte e dobra',
        dataIndex: 'hasFabricationPermission',
        width: 115,
        align: 'center' as const,
        render: (
          hasFabricationPermission: boolean,
          data: ICustomerUsersTableDataSource
        ) => (
          <>
            <Checkbox
              name="hasFabricationPermission"
              checked={hasFabricationPermission}
              onChange={(e) =>
                onChangePermission(
                  e.target.checked,
                  data.user,
                  ECustomerUserPermissionType.VIEW_FABRICATION
                )
              }
              disabled={
                data.user.loadingUpdateUser ||
                isRemovingUser ||
                restrictedToManageUsers
              }
            />
          </>
        )
      },
      {
        title: 'Visualizar pedidos',
        dataIndex: 'hasViewOrdersPermission',
        width: 100,
        align: 'center' as const,
        render: (
          hasViewOrdersPermission: boolean,
          data: ICustomerUsersTableDataSource
        ) => {
          return (
            <>
              <Checkbox
                name="hasViewOrdersPermission"
                checked={hasViewOrdersPermission}
                onChange={(e) =>
                  onChangePermission(
                    e.target.checked,
                    data.user,
                    ECustomerUserPermissionType.VIEW_ORDERS
                  )
                }
                disabled={
                  data.user.loadingUpdateUser ||
                  isRemovingUser ||
                  restrictedToManageUsers
                }
              />
            </>
          );
        }
      },
      {
        title: 'Visualizar finanças',
        dataIndex: 'hasFinancialPermission',
        width: 100,
        align: 'center' as const,
        render: (
          hasFinancialPermission: boolean,
          data: ICustomerUsersTableDataSource
        ) => {
          return (
            <>
              <Checkbox
                name="hasFinancialPermission"
                checked={hasFinancialPermission}
                onChange={(e) =>
                  onChangePermission(
                    e.target.checked,
                    data.user,
                    ECustomerUserPermissionType.FINANCIAL
                  )
                }
                disabled={
                  data.user.loadingUpdateUser ||
                  isRemovingUser ||
                  restrictedToManageUsers
                }
              />
            </>
          );
        }
      },
      {
        title: 'Visualizar preços das ordens',
        dataIndex: 'hasViewPricesPermission',
        width: 160,
        align: 'center' as const,
        render: (
          hasViewPricesPermission: boolean,
          data: ICustomerUsersTableDataSource
        ) => {
          return (
            <>
              <Checkbox
                name="hasViewPricesPermission"
                checked={hasViewPricesPermission}
                onChange={(e) =>
                  onChangePermission(
                    e.target.checked,
                    data.user,
                    ECustomerUserPermissionType.VIEW_PRICES
                  )
                }
                disabled={
                  data.user.loadingUpdateUser ||
                  isRemovingUser ||
                  restrictedToManageUsers
                }
              />
            </>
          );
        }
      },
      {
        title: 'Comprar',
        dataIndex: 'hasInputOrdersPermission',
        width: 100,
        align: 'center' as const,
        render: (
          hasInputOrdersPermission: boolean,
          data: ICustomerUsersTableDataSource
        ) => {
          return (
            <>
              <Checkbox
                name="hasInputOrdersPermission"
                checked={hasInputOrdersPermission}
                onChange={(e) => {
                  onChangePermission(
                    e.target.checked,
                    data.user,
                    ECustomerUserPermissionType.INPUT_ORDERS
                  );
                }}
                disabled={
                  data.user.loadingUpdateUser ||
                  isRemovingUser ||
                  restrictedToManageUsers
                }
              />
            </>
          );
        }
      },
      {
        title: '',
        dataIndex: '',
        width: 20,
        align: 'center' as const,
        render: (data: ICustomerUsersTableDataSource) => (
          <>
            <RemoveUserIcon
              onClick={() => {
                data.user.showRemoveUserMessage = true;
                setSelectedUser(data.user.id);
                dispatch(
                  sendTagging({
                    screenName: 'LG:GestaoDeUsuarios',
                    itemDescription: `gestaoDeUsuarios_confirmarExclusaoUsuario`,
                    itemName: `alert:confirmar_exclusao_usuario`,
                    nonInteraction: true,
                    event: 'alert'
                  })
                );
                dispatch(
                  sendTagging({
                    screenName: 'LG:GestaoDeUsuarios',
                    itemDescription: `gestaoDeUsuarios_excluirUsuario`,
                    itemName: `button:excluir_usuario`,
                    event: 'click'
                  })
                );
              }}
            >
              <Icon variant="danger" option="Trash"></Icon>
            </RemoveUserIcon>
            {data.user.showRemoveUserMessage && (
              <BannerContainer
                id={data.user.id}
                tabIndex={0}
                ref={bannerContainer}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    data.user.showRemoveUserMessage = false;
                    setSelectedUser('');
                  }
                }}
              >
                <Banner
                  bannerWithIcon
                  handleActionOne={() => {
                    dispatch(
                      sendTagging({
                        screenName: 'LG:GestaoDeUsuarios',
                        itemDescription:
                          'gestaoDeUsuarios_confirmarExclusaoUsuario_nao',
                        itemName: 'button:nao',
                        event: 'click'
                      })
                    );
                    data.user.showRemoveUserMessage = false;
                    setSelectedUser('');
                  }}
                  labelActionOne="Não"
                  handleActionTwo={() => {
                    dispatch(
                      sendTagging({
                        screenName: 'LG:GestaoDeUsuarios',
                        itemDescription:
                          'gestaoDeUsuarios_confirmarExclusaoUsuario_sim',
                        itemName: 'button:sim',
                        event: 'click'
                      })
                    );
                    data.user.showRemoveUserMessage = false;
                    removeAllUserPermissions(data.user);
                    setSelectedUser('');
                  }}
                  labelActionTwo="Sim"
                  text={`Tem certeza que deseja remover o usuário ${data.user.email}?`}
                  variant="warning"
                />
              </BannerContainer>
            )}
          </>
        )
      }
    ];

    const hideColumns: string[] = [];

    if (!canShowFabricationOption) {
      hideColumns.push('hasFabricationPermission');
    }

    return columns.filter((col) => !hideColumns.includes(col.dataIndex));
  };

  return (
    <Table columns={getColumns()} data={customerUsersData} hasPagination />
  );
};

export default CustomerUsersTable;
